import { z } from "zod";
import { Cart, AddToCartPayload, UpdateCartItemPayload, CartMergePayload } from "@/lib/types/cart";

const variantsSchema = z.object({
  style: z.string().optional(),
  size: z.string().optional(),
  color: z.string().optional(),
});

const addToCartSchema = z.object({
  productId: z.number(),
  name: z.string(),
  price: z.number().positive(),
  quantity: z.number().int().positive(),
  image: z.string().url(),
  tags: z.array(z.string()),
  variants: variantsSchema,
  stock: z.number().int().positive().optional(),
});

const updateCartItemSchema = z.object({
  itemId: z.string(),
  quantity: z.number().int().positive().optional(),
  variants: variantsSchema.optional(),
});

// Mock inventory for stock validation
const mockInventory: Record<string, number> = {};

function getMockStock(productId: number, variants: AddToCartPayload["variants"]): number {
  const key = `${productId}-${Object.values(variants).filter(Boolean).join("-")}`;
  if (!(key in mockInventory)) {
    // Generate random stock between 10 and 50
    mockInventory[key] = Math.floor(Math.random() * 41) + 10;
  }
  return mockInventory[key];
}

function updateMockStock(productId: number, variants: AddToCartPayload["variants"], quantityChange: number): void {
  const key = `${productId}-${Object.values(variants).filter(Boolean).join("-")}`;
  if (key in mockInventory) {
    mockInventory[key] = Math.max(0, mockInventory[key] - quantityChange);
  }
}

// Simulate API delay
function simulateDelay(): Promise<void> {
  const delay = Math.floor(Math.random() * 300) + 200; // 200-500ms
  return new Promise((resolve) => setTimeout(resolve, delay));
}

// Simulate random failures (10% chance)
function shouldSimulateFailure(): boolean {
  return Math.random() < 0.1;
}

/**
 * Mock API: Get cart for user or anonymous session
 */
export async function getCart(
  userId?: string
): Promise<{ data: Cart; error?: never } | { data?: never; error: string }> {
  await simulateDelay();

  if (shouldSimulateFailure()) {
    return { error: "Failed to fetch cart" };
  }

  // In a real app, this would fetch from the server
  // For now, return empty cart (client will load from localStorage for anonymous users)
  return {
    data: {
      items: [],
      userId,
      totalItems: 0,
      totalPrice: 0,
    },
  };
}

/**
 * Mock API: Add item to cart
 */
export async function addToCart(
  payload: AddToCartPayload,
  userId?: string
): Promise<{ data: Cart; error?: never } | { data?: never; error: string }> {
  // Validate payload
  const validatedFields = addToCartSchema.safeParse(payload);
  if (!validatedFields.success) {
    return { error: validatedFields.error.flatten().fieldErrors.toString() };
  }

  await simulateDelay();

  if (shouldSimulateFailure()) {
    return { error: "Failed to add item to cart" };
  }

  // Check stock availability
  const availableStock = getMockStock(payload.productId, payload.variants);
  if (payload.quantity > availableStock) {
    return { error: `Only ${availableStock} items available in stock` };
  }

  // Update mock stock
  updateMockStock(payload.productId, payload.variants, payload.quantity);

  // In a real app, this would call the backend API
  // Return success (the actual cart update happens in Zustand)
  return {
    data: {
      items: [], // Client handles the actual state
      userId,
      totalItems: 0,
      totalPrice: 0,
    },
  };
}

/**
 * Mock API: Update cart item
 */
export async function updateCartItem(
  payload: UpdateCartItemPayload,
  userId?: string
): Promise<{ data: Cart; error?: never } | { data?: never; error: string }> {
  // Validate payload
  const validatedFields = updateCartItemSchema.safeParse(payload);
  if (!validatedFields.success) {
    return { error: validatedFields.error.flatten().fieldErrors.toString() };
  }

  await simulateDelay();

  if (shouldSimulateFailure()) {
    return { error: "Failed to update cart item" };
  }

  // In a real app, this would validate stock for quantity updates
  // Return success
  return {
    data: {
      items: [],
      userId,
      totalItems: 0,
      totalPrice: 0,
    },
  };
}
// todo:ai should walk you through steps to fully integrate this with backend

/**
 * Mock API: Remove item from cart
 */
export async function removeFromCart(
  itemId: string,
  userId?: string
): Promise<{ data: Cart; error?: never } | { data?: never; error: string }> {
  await simulateDelay();

  if (shouldSimulateFailure()) {
    return { error: "Failed to remove item from cart" };
  }

  // Return success
  return {
    data: {
      items: [],
      userId,
      totalItems: 0,
      totalPrice: 0,
    },
  };
}

/**
 * Mock API: Clear entire cart
 */
export async function clearCart(
  userId?: string
): Promise<{ data: Cart; error?: never } | { data?: never; error: string }> {
  await simulateDelay();

  if (shouldSimulateFailure()) {
    return { error: "Failed to clear cart" };
  }

  return {
    data: {
      items: [],
      userId,
      totalItems: 0,
      totalPrice: 0,
    },
  };
}

/**
 * Mock API: Merge anonymous cart with user cart on login
 */
export async function mergeCart(
  payload: CartMergePayload
): Promise<{ data: Cart; error?: never } | { data?: never; error: string }> {
  await simulateDelay();

  if (shouldSimulateFailure()) {
    return { error: "Failed to merge carts" };
  }

  // In a real app, this would:
  // 1. Fetch user's existing cart from server
  // 2. Merge with anonymous cart (conflict resolution: keep higher quantity)
  // 3. Save merged cart to server
  // 4. Return merged cart

  // For now, just return the anonymous cart as-is
  return {
    data: {
      ...payload.anonymousCart,
      userId: payload.userId,
    },
  };
}

/**
 * Mock API: Check stock availability
 */
export async function checkStock(
  productId: number,
  variants: AddToCartPayload["variants"],
  quantity: number
): Promise<{ available: boolean; stock: number; error?: string }> {
  await simulateDelay();

  const availableStock = getMockStock(productId, variants);

  return {
    available: quantity <= availableStock,
    stock: availableStock,
  };
}
